import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { SectionHeading } from '../components/SectionHeading';
import { useLanguage } from '../contexts/LanguageContext';
import { profile, cv } from '../data/cv';
import { ui } from '../data/ui';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 16
  },
  visible: {
    opacity: 1,
    y: 0
  }
};
export function Home() {
  const {
    language
  } = useLanguage();
  const content = cv[language];
  const strings = ui[language];
  return <div className="w-full bg-blush-100" data-id="element-72" data-mp="24hai3s85jgg7">
      {/* Hero */}
      <section className="mx-auto max-w-6xl px-5 pb-12 pt-10 sm:px-8 sm:pt-16" data-id="element-73" data-mp="29fvhjpfkxw3l">
        <div className="grid gap-10 lg:grid-cols-[380px_1fr] lg:gap-14" data-id="element-74" data-mp="2dhgtn8kai6cq">
          <motion.div initial={{
          opacity: 0,
          scale: 0.98
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6,
          ease: 'easeOut'
        }} className="mx-auto w-full max-w-sm lg:mx-0" data-id="element-75" data-mp="2dfdrjq5v4mrn">
            <img src={profile.photo} alt={strings.hero.portraitAlt} className="aspect-[3/4] w-full object-cover object-top" data-id="element-76" data-mp="30vqe2bz7paxt" />
          </motion.div>

          <motion.div key={language} initial="hidden" animate="visible" variants={fadeUp} transition={{
          duration: 0.5,
          ease: 'easeOut'
        }} className="flex flex-col justify-center" data-id="element-77" data-mp="2qxkew24siiwn">
            <h1 className="leading-none" data-id="element-78" data-mp="2d7731oos2k21">
              <span className="block font-script text-6xl text-ink sm:text-8xl" data-id="element-79" data-mp="2d6f9ejeqqwee">
                {profile.firstName}
              </span>
              <span className="mt-2 block text-3xl font-bold uppercase tracking-[0.12em] text-ink sm:text-5xl lg:text-right" style={{
              textAlign: "center",
              paddingLeft: "147px"
            }} data-id="element-80" data-mp="285rj5zyc6jl8">
                {profile.lastName}
              </span>
            </h1>
            <div className="mt-5 h-1.5 w-full bg-blush-300" data-id="element-81" data-mp="1u9e40xh9yu9x" />
            <p className="mt-5 text-sm uppercase tracking-[0.25em] text-ink/60" data-id="element-82" data-mp="1kkqpxn7x8si6">
              {content.tagline}
            </p>
            <p className="mt-6 max-w-2xl text-sm leading-relaxed text-ink/80 sm:text-base" data-id="element-83" data-mp="1omki1741wo6q">
              {content.intro}
            </p>
            <div className="mt-8 flex flex-wrap gap-3" data-id="element-84" data-mp="1aodtihkzs0jq">
              <Link to="/portfolio" className="inline-flex items-center gap-2 rounded-full bg-ink px-6 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-blush-50 transition-colors hover:bg-ink/80" data-id="element-85" data-mp="1fkujzifnyqzz">
                {strings.hero.portfolioCta}
                <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-86" data-mp="1tm6wfjvm8ah4" />
              </Link>
              <a href="#contact" className="inline-flex items-center rounded-full border border-ink/25 px-6 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-ink transition-colors hover:bg-white" data-id="element-87" data-mp="1yjyrunvgyeq2">
                {strings.hero.contactCta}
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Body */}
      <section className="mx-auto max-w-6xl px-5 pb-20 sm:px-8" data-id="element-88" data-mp="anissumaby0n">
        <div className="grid gap-10 lg:grid-cols-[360px_1fr] lg:gap-14" data-id="element-89" data-mp="akwjapfu38fs">
          {/* Left column */}
          <div className="space-y-10" data-id="element-90" data-mp="kciskg8m2xfw">
            <div data-id="element-91" data-mp="kdlioly40vur">
              <SectionHeading variant="plain" data-id="element-92" data-mp="kd2dm5tk61y5">{strings.sections.about}</SectionHeading>
              <p className="mt-4 text-sm leading-relaxed text-ink/80" data-id="element-93" data-mp="3st58tc3hhy5l">{content.about}</p>
            </div>

            <div data-id="element-94" data-mp="3nukfzxqkagr6">
              <SectionHeading variant="plain" data-id="element-95" data-mp="3nv3sv01cueyc">{strings.sections.features}</SectionHeading>
              <dl className="mt-4 space-y-2 text-sm text-ink/80" data-id="element-96" data-mp="5oxrervar8am">
                {content.features.map(feature => <div key={feature.label} className="flex gap-2" data-id="element-97" data-mp="qw9ia9lxf0q">
                    <dt className="font-medium text-ink" data-id="element-98" data-mp="1h18ag6k8nq1v">{feature.label}:</dt>
                    <dd data-id="element-99" data-mp="1c2oo451qgc8z">{feature.value}</dd>
                  </div>)}
              </dl>
            </div>

            <div data-id="element-100" data-mp="27fg12f5c1nzn">
              <SectionHeading variant="plain" data-id="element-101" data-mp="27fzlq3mdaqhd">{strings.sections.skills}</SectionHeading>
              <div className="mt-4 space-y-6" data-id="element-102" data-mp="2cd85g8n5z2kq">
                {content.skills.map(group => <div key={group.heading} data-id="element-103" data-mp="2hargz0sh918z">
                    <h3 className="text-sm font-semibold text-ink" data-id="element-104" data-mp="2mavx2pph3tyo">{group.heading}</h3>
                    {group.lines.map(line => <p key={line} className="mt-1 text-sm text-ink/80" data-id="element-105" data-mp="2qbzg1l2ti644">
                        {line}
                      </p>)}
                    <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-ink/80 marker:text-ink/40" data-id="element-106" data-mp="302u2apg2d5pk">
                      {group.items.map(item => <li key={item} data-id="element-107" data-mp="350vk771r6yzg">{item}</li>)}
                    </ul>
                  </div>)}
              </div>
            </div>
          </div>

          {/* Right column */}
          <div className="space-y-12" data-id="element-108" data-mp="3dzz3oc4u1geh">
            <div data-id="element-109" data-mp="12f8bk1w724bc">
              <SectionHeading data-id="element-110" data-mp="12frjqqw1lec6">{strings.sections.experience}</SectionHeading>
              <ol className="mt-6 space-y-7" data-id="element-111" data-mp="yd4zbb1pavp0">
                {content.experience.map(item => <motion.li key={item.title} initial="hidden" whileInView="visible" viewport={{
                once: true,
                margin: '-60px'
              }} variants={fadeUp} transition={{
                duration: 0.45,
                ease: 'easeOut'
              }} data-id="element-112" data-mp="tdicqazzg33j">
                    <h3 className="text-base font-bold uppercase tracking-wide text-ink" data-id="element-113" data-mp="1h4o1jfnwi5pz">
                      {item.title}
                    </h3>
                    <p className="mt-1 inline-block border-b border-ink/40 pb-0.5 text-sm text-ink/70" data-id="element-114" data-mp="17ekz6ouw3fez">
                      {item.period}
                    </p>
                    {item.description && <p className="mt-3 max-w-2xl text-sm leading-relaxed text-ink/75" data-id="element-115" data-mp="202gsdjl92p70">
                        {item.description}
                      </p>}
                    {item.bullets && <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-ink/75 marker:text-ink/40" data-id="element-116" data-mp="25i6cmp7gbvg">
                        {item.bullets.map(bullet => <li key={bullet} data-id="element-117" data-mp="3pjccjdttn2y7">{bullet}</li>)}
                      </ul>}
                  </motion.li>)}
              </ol>
            </div>

            <div data-id="element-118" data-mp="3fly86cl6a4m9">
              <SectionHeading data-id="element-119" data-mp="3fmhesx5dh6kz">{strings.sections.education}</SectionHeading>
              <ol className="mt-6 space-y-6" data-id="element-120" data-mp="31mosrf4sjejc">
                {content.education.map(item => <li key={item.period} data-id="element-121" data-mp="36k870f1hnrba">
                    <p className="text-sm font-bold uppercase tracking-wide text-ink" data-id="element-122" data-mp="36l9tdfnyg08b">
                      {item.period}
                    </p>
                    <p className="mt-1 text-base italic text-ink/85" data-id="element-123" data-mp="14u6j4rhz8noa">{item.school}</p>
                    <p className="text-base italic text-ink/70" data-id="element-124" data-mp="1exbu1hq9yqze">{item.detail}</p>
                  </li>)}
              </ol>
            </div>
          </div>
        </div>
      </section>
    </div>;
}